import { Collection } from 'app/providers/storage-api/collection.service';
import { LoggerService } from 'app/services/logger.service';
import {
  ChatSessionWithName,
  ResearchIdea,
  SpotlightAgentChatService,
} from 'app/services/spotlight-agent-chat.service';

interface DataSourceLike {
  readonly id: string;
  readonly name: string;
  readonly projectId: string;
  readonly collectionId: string;
  readonly documentCount: number;
  readonly analysisCount: number;
  readonly isDisabled?: boolean;
  readonly incompatibleReason?: string;
  readonly contentChannel?: string;
  readonly hasSections?: boolean;
}

interface AnalysisResult {
  readonly ok: boolean;
  readonly reason?: string;
  readonly researchIdeas: ResearchIdea[];
}

/**
 * Helper for datasource mapping, analysis handling, and chat session retrieval.
 */
export class SpotlightAssistantDataSourceHelper {
  constructor(private readonly logger: LoggerService) {}

  mapCollectionsToDataSources(
    projectId: string,
    collections: Collection[],
    incompatibleReason: string
  ): DataSourceLike[] {
    return collections
      .filter((collection: Collection) => collection.documentCount > 0)
      .map((collection: Collection) => {
        const hasAnalysis: boolean = collection.analysisCount > 0;
        return {
          id: collection.collectionName,
          name: collection.displayName,
          projectId,
          collectionId: collection.collectionName,
          documentCount: collection.documentCount,
          analysisCount: collection.analysisCount,
          isDisabled: !hasAnalysis,
          incompatibleReason: hasAnalysis ? undefined : incompatibleReason,
          contentChannel: collection.contentChannel,
          hasSections: collection.hasSections,
        };
      });
  }

  markDataSourceIncompatible(
    sources: DataSourceLike[],
    sourceId: string,
    fallbackReason: string,
    reason?: string
  ): DataSourceLike[] {
    return sources.map((source: DataSourceLike) =>
      source.id === sourceId
        ? {
            ...source,
            isDisabled: true,
            incompatibleReason: reason ?? fallbackReason,
          }
        : source
    );
  }

  async loadResearchIdeas(
    spotlightAgentChatService: SpotlightAgentChatService,
    projectId: string,
    collectionId: string,
    fallbackReason: string,
    contentChannel?: string,
    hasSections?: boolean
  ): Promise<AnalysisResult> {
    try {
      const analysis = await spotlightAgentChatService.analyseCollection(
        projectId,
        collectionId,
        contentChannel,
        hasSections
      );
      return {
        ok: true,
        researchIdeas: analysis.researchIdeas || [],
      };
    } catch (error: unknown) {
      return {
        ok: false,
        reason: this.getAnalysisFailureReason(error, fallbackReason),
        researchIdeas: [],
      };
    }
  }

  async loadChatSessions(
    spotlightAgentChatService: SpotlightAgentChatService,
    projectId: string,
    collectionId?: string
  ): Promise<ChatSessionWithName[]> {
    try {
      return await spotlightAgentChatService.getChatSessions(projectId, collectionId);
    } catch (error) {
      this.logger.error('Failed to load chat history:', { error });
      return [];
    }
  }

  private getAnalysisFailureReason(error: unknown, fallbackReason: string): string {
    if (typeof error === 'string') {
      return error;
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    if (this.isRecord(error)) {
      const nestedError: unknown = error.error;
      if (typeof nestedError === 'string') {
        return nestedError;
      }
      if (this.isRecord(nestedError)) {
        const nestedMessage: unknown = nestedError.message;
        if (typeof nestedMessage === 'string' && nestedMessage.length > 0) {
          return nestedMessage;
        }
      }
      const message: unknown = error.message;
      if (typeof message === 'string' && message.length > 0) {
        return message;
      }
    }
    return fallbackReason;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }
}
